import React, { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react"
import { createPortal } from "react-dom"
import {
  Bell,
  ChevronDown,
  History,
  LayoutGrid,
  Plus,
  Search,
  Settings,
  UserPlus,
} from "lucide-react"
import { Button } from "./ui/button"
import { ActiniumLogo } from "./actinium-logo"
import { cn } from "../lib/utils"
import { canAccessModule, visibleModules } from "../lib/book-nav"
import { appAlert, appPrompt } from "../lib/app-dialogs"
import { usePageLoading } from "../lib/page-loading"

function csrfToken() {
  return document.querySelector("meta[name='csrf-token']")?.getAttribute("content") || ""
}

function initials(name) {
  if (!name) return "?"
  const parts = String(name).trim().split(/\s+/)
  if (parts.length >= 2) return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  return parts[0].slice(0, 2).toUpperCase()
}

const MENU_PANEL =
  "fixed z-[180] max-h-80 min-w-[240px] max-w-[min(100vw-2rem,320px)] overflow-auto rounded-xl border border-border bg-card py-1 text-[13px] shadow-soft"

const MENU_LINK = "block w-full px-3 py-2 text-left text-foreground transition-colors hover:bg-muted"

const MENU_BTN =
  "w-full px-3 py-2 text-left text-foreground transition-colors hover:bg-muted disabled:cursor-not-allowed disabled:opacity-50"

function headerMenuCoords(anchor) {
  if (!anchor) return null
  const r = anchor.getBoundingClientRect()
  const pad = 8
  const gap = 4
  const minW = 240
  const maxW = Math.min(320, window.innerWidth - pad * 2)
  const width = Math.min(Math.max(minW, r.width), maxW)
  let left = r.right - width
  if (left < pad) left = pad
  if (left + width > window.innerWidth - pad) {
    left = Math.max(pad, window.innerWidth - pad - width)
  }
  return { top: r.bottom + gap, left, minWidth: width }
}

function HeaderMenuPanel({ anchorRef, panelRef, role = "menu", as: Comp = "div", className, children }) {
  const [coords, setCoords] = useState(() => headerMenuCoords(anchorRef?.current))

  useLayoutEffect(() => {
    const place = () => setCoords(headerMenuCoords(anchorRef?.current))
    place()
    window.addEventListener("resize", place)
    window.addEventListener("scroll", place, true)
    return () => {
      window.removeEventListener("resize", place)
      window.removeEventListener("scroll", place, true)
    }
  }, [anchorRef])

  if (typeof document === "undefined" || !coords) return null

  return createPortal(
    <Comp
      ref={panelRef}
      role={role}
      className={cn(MENU_PANEL, className)}
      data-header-menu=""
      style={{ top: coords.top, left: coords.left, minWidth: coords.minWidth }}
    >
      {children}
    </Comp>,
    document.body,
  )
}

export function TopNav({ userName, access = {} }) {
  const [orgs, setOrgs] = useState([])
  const [openMenu, setOpenMenu] = useState(null)
  const [notifications, setNotifications] = useState([])
  const [navBusy, setNavBusy] = useState(false)
  usePageLoading(navBusy, "Switching company…")

  const orgMenuRef = useRef(null)
  const quickMenuRef = useRef(null)
  const historyMenuRef = useRef(null)
  const notifMenuRef = useRef(null)
  const settingsMenuRef = useRef(null)
  const appsMenuRef = useRef(null)
  const profileMenuRef = useRef(null)
  const menuPanelRef = useRef(null)

  const currentIdMeta =
    typeof document !== "undefined" ? document.querySelector("meta[name='current-organization-id']")?.getAttribute("content") : null
  const currentId =
    currentIdMeta != null && String(currentIdMeta).trim() !== "" && !Number.isNaN(Number(currentIdMeta))
      ? Number(currentIdMeta)
      : null

  const toggle = useCallback((id) => {
    setOpenMenu((prev) => (prev === id ? null : id))
  }, [])

  useEffect(() => {
    if (!openMenu) return
    const refs = {
      org: orgMenuRef,
      quick: quickMenuRef,
      history: historyMenuRef,
      notif: notifMenuRef,
      settings: settingsMenuRef,
      apps: appsMenuRef,
      profile: profileMenuRef,
    }
    const ref = refs[openMenu]
    const onDoc = (e) => {
      const inTrigger = ref?.current?.contains(e.target)
      const inMenu = menuPanelRef.current?.contains(e.target)
      if (!inTrigger && !inMenu) setOpenMenu(null)
    }
    document.addEventListener("mousedown", onDoc)
    return () => document.removeEventListener("mousedown", onDoc)
  }, [openMenu])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch("/api/organizations", {
          headers: { Accept: "application/json" },
          credentials: "same-origin",
        })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setOrgs(Array.isArray(data) ? data : [])
      } catch {
        /* ignore */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        const res = await fetch("/api/user_notifications", {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        })
        if (!res.ok) return
        const data = await res.json()
        if (!cancelled) setNotifications(Array.isArray(data) ? data : [])
      } catch {
        /* ignore */
      }
    })()
    return () => {
      cancelled = true
    }
  }, [])

  const currentOrg = orgs.find((o) => Number(o.id) === currentId) || orgs[0]
  const orgLabel = currentOrg?.name || (access?.needs_company ? "No company" : "Default")
  const showOrgMenu = orgs.length > 0 || access?.can_create_company

  async function selectOrg(id) {
    setOpenMenu(null)
    if (Number(id) === currentId) return
    setNavBusy(true)
    try {
      const res = await fetch(`/api/organizations/${id}/select`, {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
      })
      if (res.ok) {
        const data = await res.json().catch(() => ({}))
        const next = data.default_home_path || "/dashboard"
        window.location.assign(next)
        return
      }
    } finally {
      setNavBusy(false)
    }
  }

  async function createOrg() {
    const name = await appPrompt("New company name", "", { title: "Create company", placeholder: "Company name" })
    if (!name?.trim()) return
    setNavBusy(true)
    try {
      const res = await fetch("/api/organizations", {
        method: "POST",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ organization: { name: name.trim(), currency: "INR" } }),
      })
      const data = await res.json().catch(() => ({}))
      if (!res.ok) {
        await appAlert(data.message || data.error || "Could not create company", { title: "Create company" })
        return
      }
      window.location.reload()
      return
    } catch {
      await appAlert("Could not create company", { title: "Create company" })
    } finally {
      setNavBusy(false)
    }
  }

  async function markNotificationRead(id) {
    try {
      await fetch(`/api/user_notifications/${id}`, {
        method: "PATCH",
        credentials: "same-origin",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken(),
        },
        body: JSON.stringify({ user_notification: { read: true } }),
      })
      setNotifications((prev) => prev.map((n) => (Number(n.id) === Number(id) ? { ...n, read_at: new Date().toISOString() } : n)))
    } catch {
      /* ignore */
    }
  }

  async function openNotification(n) {
    if (!n.read_at) await markNotificationRead(n.id)
    setOpenMenu(null)
    if (n.link_path) {
      const dest = n.link_allowed === false ? access?.default_home_path || "/dashboard" : n.link_path
      window.location.assign(dest)
    }
  }

  const unreadCount = notifications.filter((n) => !n.read_at).length
  const mod = (slug) => canAccessModule(access, slug)
  const appModules = visibleModules(access)
  const setupOnly = Boolean(access?.needs_company)
  const homeHref = setupOnly ? "/admin" : "/dashboard"

  return (
    <header className="sticky top-0 z-40 flex h-[52px] shrink-0 items-center gap-2 border-b border-border bg-card/90 px-2 text-sm text-foreground shadow-nav backdrop-blur-md sm:gap-3 sm:px-3 md:px-4">
      <a href={homeHref} className="flex shrink-0 items-center gap-2 rounded-lg py-1 pr-1 transition-colors hover:bg-muted sm:pr-2">
        <ActiniumLogo size={32} className="rounded-lg shadow-sm" />
        <span className="hidden text-[15px] font-semibold tracking-tight text-foreground sm:inline">Actinium Book</span>
      </a>

      <div className="relative shrink-0" ref={historyMenuRef}>
        {!setupOnly ? (
        <>
        <Button
          type="button"
          variant="ghost"
          className="h-9 w-9 shrink-0 p-0 text-muted-foreground"
          aria-label="Recent activity and shortcuts"
          aria-expanded={openMenu === "history"}
          onClick={() => toggle("history")}
        >
          <History className="h-4 w-4" strokeWidth={1.75} />
        </Button>
        {openMenu === "history" ? (
          <HeaderMenuPanel anchorRef={historyMenuRef} panelRef={menuPanelRef} role="menu">
            {mod("home") ? (
              <a href="/dashboard" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                Dashboard
              </a>
            ) : null}
            {mod("sales") ? (
              <a href="/sales/invoices" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                Sales · Invoices
              </a>
            ) : null}
            {mod("purchases") ? (
              <a href="/purchases/bills" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                Purchases · Bills
              </a>
            ) : null}
            {mod("accountant") ? (
              <a href="/accountant/manual-journals" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                Accountant · Manual journals
              </a>
            ) : null}
            {mod("documents") ? (
              <a href="/documents/library" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                Documents · Library
              </a>
            ) : null}
          </HeaderMenuPanel>
        ) : null}
        </>
        ) : null}
      </div>

      <div className="relative hidden min-w-0 flex-1 sm:block sm:max-w-xl">
        {!setupOnly ? (
        <>
        <Search
          className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground"
          strokeWidth={2}
        />
        <input
          type="search"
          readOnly
          onFocus={() => window.location.assign("/dashboard")}
          placeholder="Search in Books ( / )"
          className="h-9 w-full cursor-pointer rounded-lg border border-input bg-background pl-8 pr-3 text-[14px] text-foreground placeholder:text-muted-foreground outline-none transition-shadow focus:border-primary focus:ring-2 focus:ring-ring/20"
          aria-label="Open dashboard search (full search coming soon)"
        />
        </>
        ) : null}
      </div>

      <div className="hidden h-6 w-px shrink-0 bg-border xl:block" />

      <div className="ml-auto hidden shrink-0 items-center gap-2 text-sm text-muted-foreground xl:flex">
        <span className="max-w-[120px] truncate 2xl:max-w-[140px]">Your premium trial plan</span>
        <a href="#" className="font-medium text-primary underline-offset-2 hover:underline" title="Contact sales for subscription">
          Subscribe
        </a>
      </div>

      <div className="hidden h-6 w-px shrink-0 bg-border xl:block" />

      <div className="relative min-w-0 shrink" ref={orgMenuRef}>
        <button
          type="button"
          onClick={() => showOrgMenu && toggle("org")}
          disabled={!showOrgMenu}
          className={cn(
            "flex max-w-[min(42vw,200px)] items-center gap-1 truncate rounded-lg border border-transparent px-1.5 py-1.5 text-left text-[13px] font-medium transition-colors sm:max-w-[220px] sm:px-2",
            showOrgMenu ? "text-foreground hover:bg-muted" : "cursor-not-allowed text-muted-foreground",
          )}
          aria-expanded={openMenu === "org"}
          aria-haspopup="listbox"
          aria-label="Switch company"
        >
          <span className="min-w-0 flex-1 truncate">{orgLabel}</span>
          <ChevronDown className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
        </button>
        {openMenu === "org" && showOrgMenu ? (
          <HeaderMenuPanel anchorRef={orgMenuRef} panelRef={menuPanelRef} as="ul" role="listbox">
            {orgs.length === 0 ? (
              <li className="px-3 py-2 text-xs leading-relaxed text-muted-foreground">
                No company yet. Use Register company below, or open Administration → Register company in the sidebar.
              </li>
            ) : (
              orgs.map((o) => (
                <li key={o.id} role="option" aria-selected={Number(o.id) === currentId}>
                  <button
                    type="button"
                    className={cn(
                      MENU_BTN,
                      Number(o.id) === currentId && "bg-sidebar-accent font-medium text-primary",
                    )}
                    onClick={() => selectOrg(o.id)}
                  >
                    {o.name}
                  </button>
                </li>
              ))
            )}
            {access?.needs_company && access?.can_create_company ? (
              <li className="border-t border-border">
                <a href="/admin" className="block px-3 py-2 font-medium text-primary hover:bg-muted" onClick={() => setOpenMenu(null)}>
                  Register company…
                </a>
              </li>
            ) : null}
            {access?.can_create_company && orgs.length > 0 ? (
              <li className="border-t border-border">
                <button type="button" className={cn(MENU_BTN, "text-primary")} onClick={() => { setOpenMenu(null); createOrg() }}>
                  + New company (quick)
                </button>
              </li>
            ) : null}
          </HeaderMenuPanel>
        ) : null}
      </div>

      {!setupOnly ? (
      <div className="relative shrink-0" ref={quickMenuRef}>
        <Button
          type="button"
          className="h-9 w-9 shrink-0 p-0 shadow-sm"
          aria-label="Quick create"
          aria-expanded={openMenu === "quick"}
          onClick={() => toggle("quick")}
        >
          <Plus className="h-5 w-5" strokeWidth={2} />
        </Button>
        {openMenu === "quick" ? (
          <HeaderMenuPanel anchorRef={quickMenuRef} panelRef={menuPanelRef} role="menu">
            {mod("sales") ? (
              <a href="/sales/customers" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                New customer
              </a>
            ) : null}
            {mod("sales") ? (
              <a href="/sales/invoices" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                New invoice
              </a>
            ) : null}
            {mod("purchases") ? (
              <a href="/purchases/vendors" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                New vendor
              </a>
            ) : null}
            {mod("purchases") ? (
              <a href="/purchases/bills" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                New bill
              </a>
            ) : null}
            {mod("items") ? (
              <a href="/items/catalog" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                New item
              </a>
            ) : null}
            {mod("credit-notes") ? (
              <a href="/credit-notes/all" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                New credit note
              </a>
            ) : null}
          </HeaderMenuPanel>
        ) : null}
      </div>
      ) : null}

      <div className="flex shrink-0 items-center gap-0.5">
        {!setupOnly && access?.can_manage_company_settings ? (
          <a
            href="/admin#invite-team"
            className="inline-flex h-9 w-9 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
            aria-label="Invite team"
            title="Invite team"
          >
            <UserPlus className="h-4 w-4" strokeWidth={1.75} />
          </a>
        ) : null}

        <div className="relative" ref={notifMenuRef}>
          <Button
            type="button"
            variant="ghost"
            className="relative h-9 w-9 p-0 text-muted-foreground"
            aria-label="Notifications"
            aria-expanded={openMenu === "notif"}
            onClick={() => toggle("notif")}
          >
            <Bell className="h-4 w-4" strokeWidth={1.75} />
            {unreadCount > 0 ? (
              <span className="absolute right-0.5 top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-0.5 text-[10px] font-semibold text-primary-foreground">
                {unreadCount > 9 ? "9+" : unreadCount}
              </span>
            ) : null}
          </Button>
          {openMenu === "notif" ? (
            <HeaderMenuPanel anchorRef={notifMenuRef} panelRef={menuPanelRef} role="menu">
              {notifications.length === 0 ? (
                <div className="px-3 py-4 text-xs text-muted-foreground">No notifications yet.</div>
              ) : (
                <ul className="divide-y divide-border">
                  {notifications.map((n) => (
                    <li key={n.id}>
                      <button
                        type="button"
                        className={cn(MENU_BTN, !n.read_at && "bg-primary/[0.04] font-medium")}
                        onClick={() => openNotification(n)}
                      >
                        <span className="block text-[13px] text-foreground">{n.title}</span>
                        {n.body ? <span className="mt-0.5 block text-[11px] leading-snug text-muted-foreground">{n.body}</span> : null}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </HeaderMenuPanel>
          ) : null}
        </div>

        <div className="relative" ref={settingsMenuRef}>
          <Button
            type="button"
            variant="ghost"
            className="h-9 w-9 p-0 text-muted-foreground"
            aria-label="Settings"
            aria-expanded={openMenu === "settings"}
            onClick={() => toggle("settings")}
          >
            <Settings className="h-4 w-4" strokeWidth={1.75} />
          </Button>
          {openMenu === "settings" ? (
            <HeaderMenuPanel anchorRef={settingsMenuRef} panelRef={menuPanelRef} role="menu">
              {access?.can_manage_company_settings ? (
                <a href="/admin" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                  Organization &amp; team
                </a>
              ) : null}
              {mod("accountant") ? (
                <a href="/accountant/bookkeeping-settings" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                  Bookkeeping settings
                </a>
              ) : null}
              {mod("accountant") ? (
                <a href="/accountant/chart-of-accounts" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                  Chart of accounts
                </a>
              ) : null}
              {mod("accountant") ? (
                <a href="/accountant/fiscal-periods" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                  Fiscal periods
                </a>
              ) : null}
            </HeaderMenuPanel>
          ) : null}
        </div>
      </div>

      <div className="relative shrink-0" ref={profileMenuRef}>
        <button
          type="button"
          className={cn(
            "flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-border bg-muted text-[12px] font-semibold text-primary transition-colors hover:bg-muted/80",
          )}
          title={userName || "User"}
          aria-label="Account menu"
          aria-expanded={openMenu === "profile"}
          onClick={() => toggle("profile")}
        >
          {initials(userName)}
        </button>
        {openMenu === "profile" ? (
          <HeaderMenuPanel anchorRef={profileMenuRef} panelRef={menuPanelRef} role="menu">
            <div className="border-b border-border px-3 py-2 text-xs text-muted-foreground">
              <div className="font-medium text-foreground">{userName || "Signed in"}</div>
            </div>
            <a href="/dashboard" className={MENU_LINK} onClick={() => setOpenMenu(null)}>
              Dashboard
            </a>
            <form action="/logout" method="post" className="border-t border-border">
              <input type="hidden" name="authenticity_token" value={csrfToken()} />
              <input type="hidden" name="_method" value="delete" />
              <button type="submit" className={cn(MENU_BTN, "text-left text-red-700 hover:bg-red-50")}>
                Log out
              </button>
            </form>
          </HeaderMenuPanel>
        ) : null}
      </div>

      {!setupOnly ? (
      <div className="relative shrink-0" ref={appsMenuRef}>
        <Button
          type="button"
          variant="ghost"
          className="h-9 w-9 shrink-0 p-0 text-muted-foreground"
          aria-label="Modules"
          aria-expanded={openMenu === "apps"}
          onClick={() => toggle("apps")}
        >
          <LayoutGrid className="h-4 w-4" strokeWidth={1.75} />
        </Button>
        {openMenu === "apps" ? (
          <HeaderMenuPanel anchorRef={appsMenuRef} panelRef={menuPanelRef} role="menu">
            {appModules.map((item) => (
              <a key={item.slug} href={item.href} className={MENU_LINK} onClick={() => setOpenMenu(null)}>
                {item.name}
              </a>
            ))}
          </HeaderMenuPanel>
        ) : null}
      </div>
      ) : null}
    </header>
  )
}
